import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import Icon from "../AppIcon";

/**
 * Global confirmation modal for delete, activate/deactivate, and other confirm actions.
 * @param {boolean} isOpen - Whether modal is visible
 * @param {string} title - Modal title
 * @param {string} message - Confirmation message
 * @param {function} onConfirm - Called when user confirms
 * @param {function} onCancel - Called when user cancels
 * @param {string} confirmText - Confirm button label (default "Confirm")
 * @param {string} cancelText - Cancel button label (default "Cancel")
 * @param {string} confirmVariant - "danger" | "primary" | "success" (default "danger" for delete)
 * @param {boolean} loading - Show loading state on confirm button
 */
const ConfirmationModal = ({
  isOpen,
  title = "Confirm Action",
  message,
  onConfirm,
  onCancel,
  confirmText = "Confirm",
  cancelText = "Cancel",
  confirmVariant = "danger",
  loading = false,
}) => {
  if (!isOpen) return null;

  const getConfirmButtonStyles = (variant) => {
    switch (variant) {
      case "danger":
        return "bg-error text-error-foreground hover:bg-error/90 disabled:opacity-50";
      case "primary":
        return "bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50";
      case "success":
        return "bg-success text-white hover:bg-success/90 disabled:opacity-50";
      default:
        return "bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50";
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 bg-black/60 flex items-center justify-center z-[10050] p-4"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="bg-card rounded-xl shadow-xl border border-border w-full max-w-md overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-6 border-b border-border">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-warning/20 flex items-center justify-center">
                  <Icon name="TriangleAlert" size={20} className="text-warning" />
                </div>
                <h3 className="text-lg font-semibold text-text-primary">{title}</h3>
              </div>
              <button
                type="button"
                onClick={onCancel}
                disabled={loading}
                className="p-2 rounded-lg hover:bg-muted text-text-secondary transition-colors"
              >
                <Icon name="X" size={18} />
              </button>
            </div>
            <div className="p-6">
              <p className="text-text-secondary leading-relaxed">{message}</p>
            </div>
            <div className="flex items-center justify-end gap-3 p-6 bg-muted/50 border-t border-border">
              <button
                type="button"
                onClick={onCancel}
                disabled={loading}
                className="px-4 py-2 rounded-lg border border-border hover:bg-muted text-text-primary transition-colors"
              >
                {cancelText}
              </button>
              <button
                type="button"
                onClick={onConfirm}
                disabled={loading}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${getConfirmButtonStyles(confirmVariant)}`}
              >
                {loading ? "…" : confirmText}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ConfirmationModal;
